import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Wine region coordinates database
const WINE_REGIONS = {
  // France
  'Burgundy': { lat: 47.0403, lng: 4.8667, country: 'France' },
  'Bordeaux': { lat: 44.8378, lng: -0.5792, country: 'France' },
  'Champagne': { lat: 49.2628, lng: 4.0347, country: 'France' },
  'Loire Valley': { lat: 47.2692, lng: 1.0833, country: 'France' },
  'Rhône Valley': { lat: 44.5333, lng: 4.7167, country: 'France' },
  'Alsace': { lat: 48.5734, lng: 7.7521, country: 'France' },
  
  // Italy
  'Tuscany': { lat: 43.7711, lng: 11.2486, country: 'Italy' },
  'Piedmont': { lat: 44.7013, lng: 7.8581, country: 'Italy' },
  'Veneto': { lat: 45.4398, lng: 12.3319, country: 'Italy' },
  'Sicily': { lat: 37.5999, lng: 14.0154, country: 'Italy' },
  'Umbria': { lat: 43.1122, lng: 12.3888, country: 'Italy' },
  
  // Spain
  'Rioja': { lat: 42.4630, lng: -2.4453, country: 'Spain' },
  'Ribera del Duero': { lat: 41.6167, lng: -4.0500, country: 'Spain' },
  'Priorat': { lat: 41.2833, lng: 0.8333, country: 'Spain' },
  
  // Portugal
  'Douro': { lat: 41.2033, lng: -7.7811, country: 'Portugal' },
  'Dão': { lat: 40.4167, lng: -7.9167, country: 'Portugal' },
  
  // Germany
  'Mosel': { lat: 49.9167, lng: 6.9167, country: 'Germany' },
  'Rheingau': { lat: 50.0000, lng: 8.0833, country: 'Germany' },
  
  // USA
  'Napa Valley': { lat: 38.5025, lng: -122.2654, country: 'USA' },
  'Sonoma County': { lat: 38.5816, lng: -122.8047, country: 'USA' },
  'Oregon': { lat: 45.3311, lng: -121.7113, country: 'USA' },
  'Washington': { lat: 46.7296, lng: -120.7458, country: 'USA' },
  
  // Australia
  'Barossa Valley': { lat: -34.5133, lng: 138.9619, country: 'Australia' },
  'Hunter Valley': { lat: -32.8333, lng: 151.2833, country: 'Australia' },
  'Margaret River': { lat: -33.9500, lng: 115.0667, country: 'Australia' },
  
  // New Zealand
  'Marlborough': { lat: -41.5000, lng: 174.0000, country: 'New Zealand' },
  'Central Otago': { lat: -45.0333, lng: 169.2000, country: 'New Zealand' },
  
  // Chile
  'Maipo Valley': { lat: -33.6500, lng: -70.9000, country: 'Chile' },
  'Colchagua Valley': { lat: -34.6000, lng: -71.0000, country: 'Chile' },
  
  // Argentina
  'Mendoza': { lat: -32.8895, lng: -68.8458, country: 'Argentina' },
  'Salta': { lat: -24.7859, lng: -65.4117, country: 'Argentina' },
  
  // South Africa
  'Stellenbosch': { lat: -33.9321, lng: 18.8602, country: 'South Africa' },
  'Paarl': { lat: -33.7433, lng: 18.9644, country: 'South Africa' },
};

const WineryMap = ({ drink }) => {
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const findWineryLocation = () => {
      setLoading(true);
      
      // First, check if we have exact coordinates in the drink data
      if (drink.coordinates) {
        setCoordinates({
          lat: drink.coordinates.lat,
          lng: drink.coordinates.lng,
          source: 'exact'
        });
        setLoading(false);
        return;
      }

      // Look for region match in our database
      const region = drink.region || '';
      const regionKey = Object.keys(WINE_REGIONS).find(key => 
        region.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(region.toLowerCase())
      );

      if (regionKey) {
        setCoordinates({
          ...WINE_REGIONS[regionKey],
          source: 'region',
          regionName: regionKey
        });
      } else {
        // Fallback: try to geocode the region name
        // For now, we'll set to null if no match found
        setCoordinates(null);
      }
      
      setLoading(false);
    };

    if (drink.region) {
      findWineryLocation();
    } else {
      setLoading(false);
    }
  }, [drink]);

  if (loading) {
    return (
      <div className="detail-card-bg" style={{padding: '1.5rem', borderRadius: '0.5rem'}}>
        <h3 className="detail-section-header" style={{fontSize: '1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          🗺️ Winery Location
        </h3>
        <div style={{height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--warm-beige)', borderRadius: '0.5rem'}}>
          <p style={{color: 'var(--text-muted)'}}>Loading map...</p>
        </div>
      </div>
    );
  }

  if (!coordinates) {
    return (
      <div className="detail-card-bg" style={{padding: '1.5rem', borderRadius: '0.5rem'}}>
        <h3 className="detail-section-header" style={{fontSize: '1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          🗺️ Winery Location
        </h3>
        <div style={{height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--warm-beige)', borderRadius: '0.5rem', flexDirection: 'column', gap: '0.5rem'}}>
          <span style={{fontSize: '3rem'}}>🗺️</span>
          <p style={{color: 'var(--text-muted)', textAlign: 'center'}}>
            Location information not available for {drink.region || 'this wine'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="detail-card-bg" style={{padding: '1.5rem', borderRadius: '0.5rem'}}>
      <h3 className="detail-section-header" style={{fontSize: '1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
        🗺️ Winery Location
      </h3>
      
      <div style={{height: '300px', borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid var(--border-color)'}}>
        <MapContainer
          center={[coordinates.lat, coordinates.lng]}
          zoom={coordinates.source === 'exact' ? 15 : 10}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={[coordinates.lat, coordinates.lng]}>
            <Popup>
              <div style={{textAlign: 'center'}}>
                <strong>{drink.producer || drink.winery}</strong><br />
                {coordinates.regionName || drink.region}<br />
                {coordinates.country && <em>{coordinates.country}</em>}
                {coordinates.source === 'region' && (
                  <div style={{fontSize: '0.8rem', color: '#666', marginTop: '0.5rem'}}>
                    Approximate regional location
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        </MapContainer>
      </div>
      
      <div style={{marginTop: '0.75rem', fontSize: '0.875rem', color: 'var(--text-muted)', textAlign: 'center'}}>
        {coordinates.source === 'exact' ? (
          <span>📍 Exact winery location</span>
        ) : (
          <span>📍 Approximate location in {coordinates.regionName || drink.region}</span>
        )}
      </div>
    </div>
  );
};

export default WineryMap;
